import * as React from 'react';
import * as navigate from 'app/area/shared/navigation';
import { Paper, PromptConfirmation } from 'ui';
import { withPrompt, PromptProps, SpinLoader } from 'app/components';
import { STATE } from 'app/constants';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { containerFactory, DefaultContainerProps } from 'app/infrastructure/redux';
import { hasTranslationsLoaded, translationChanged } from 'app/infrastructure/translate/selectors';
import { OptionsProps, withOptions } from 'app/components/options-handler';
import { subscriberGroupsTranslation } from '../constants';
import { SubscriberGroupsState } from '../types-store';
import { SubscriberGroupFormSections } from './components/subscriber-group-form-sections';
import { actionCreators } from './actions';
import { SubscriberGroupFormSelectorProps, selectors } from './selectors';
import { SubscriberGroupItem } from './types-store';
import { scrollToTop } from 'app/infrastructure/platform/routing';
import { SubscriberGroupActionType } from 'app/area/subscriber-groups/form/types';
import { getSubscriberGroupAdminAccess } from 'app/area/subscriber-groups/get-access';
import { MergeSubscriberGroupModal } from 'app/area/subscriber-groups/shared/merge-subscriber-group/merge-subscriber-group-modal';
import { fieldName } from 'app/area/subscriber-groups/shared/merge-subscriber-group/constants';
import { replaceTemplateString } from 'app/infrastructure/platform';

type SubscriberGroupFormProps = DefaultContainerProps
  & OptionsProps
  & PromptProps
  & SubscriberGroupsState
  & SubscriberGroupFormSelectorProps
  & typeof actionCreators;

interface SubscriberGroupFormState {
  showTranslations: boolean;
}

class SubscriberGroupFormComponent extends React.PureComponent<SubscriberGroupFormProps, SubscriberGroupFormState> {
  public static statePath: string = STATE.subscriberGroups;

  public state: Readonly<SubscriberGroupFormState> = {
    showTranslations: false
  };

  public componentDidMount() {
    scrollToTop();
    this.props.loadLanguageOptions();

    if (hasTranslationsLoaded(this.props.translation)) {
      this.initForm();
    }
  }

  public componentDidUpdate(prevProps: SubscriberGroupFormProps) {
    if (this.props.id !== prevProps.id ||
      translationChanged(this.props.translation, prevProps.translation)) {
      this.initForm();
    }

    if (prevProps?.form?.validation?.language !== this.props?.form?.validation?.language
      || prevProps?.form?.validation?.longDescription !== this.props?.form?.validation?.longDescription
    ) {
      this.updateTranslationsToggle();
    }
  }

  public componentWillUnmount() {
    this.props.clearForm();
  }

  private initForm = () => {
    const { id, sharedState, createSubscriberGroup, loadSubscriberGroup, displayError, translation } = this.props;

    const subscriberGroupAccess = getSubscriberGroupAdminAccess(sharedState);
    if (!subscriberGroupAccess) {
      displayError(translation.no_access_to_subscriber_groups);
      navigate.toStartupView(true);
      return;
    }

    if (isDefinedIncludingZero(id)) {
      loadSubscriberGroup(id);
    } else {
      createSubscriberGroup();
    }
  };

  private switchTranslationsToggle = (showTranslations: boolean) => this.setState({ showTranslations });

  private updateTranslationsToggle = () => {
    if (!!this.props?.form?.validation?.language || !!this.props?.form?.validation?.longDescription) {
      this.switchTranslationsToggle(true);
    }
  };

  private saveSubscriberGroup = (subscriberGroupItem: SubscriberGroupItem) => {
    const {
      params: { actionType } = {}, saveSubscriberGroup, translation } = this.props;

    const actionTypeId = SubscriberGroupActionType[actionType];
    const successMessage = (isDefinedIncludingZero(subscriberGroupItem?.id)
      && actionTypeId === SubscriberGroupActionType.edit)
      ? translation.subscriber_group_updated
      : translation.subscriber_group_created;

    saveSubscriberGroup(actionTypeId, subscriberGroupItem, successMessage, translation.subscriber_group_validation_error);
  };

  private deleteSubscriberGroupItem = (uncheckSelectItem: boolean) => {
    const {
      form: { mergeSubscriberGroup: { name, reassignReferencesToCode: { value = undefined } = {} } },
      translation, deleteSubscriberGroup, id
    } = this.props;

    deleteSubscriberGroup(
      id,
      replaceTemplateString(translation.subscriber_group_deleted, name),
      !uncheckSelectItem ? value : undefined
    );
  };

  private mergeSubscriberGroupItem = () => {
    this.resetValidation(fieldName);
    this.props.updateMergeSubscriberGroup({
      ...this.props.form.mergeSubscriberGroup,
      showMergeSubscriberGroup: !this.props.form.mergeSubscriberGroup?.showMergeSubscriberGroup
    });
  };

  private resetValidation = (key?: string) =>
    this.props.resetValidation(this.props.id, key);

  public render() {
    const { section, sharedState, form, form: { mergeSubscriberGroup }, translation,
      options, shouldPrompt,
      params: { actionType },
      changeLanguage, copyLanguageToAll, changeLongDescription, copyLongDescriptionToAll,
      updateItem, updateExternalMemberItem, removeExternalMember, addExternalMember,
      resetValidation, removeAllExternalMembers,
      updateValidation, displayError
    } = this.props;

    const mainLanguage = sharedState?.language?.value ?? undefined;
    const locale = sharedState?.locale || '';
    const displayMergeSubscriberGroupModal = mergeSubscriberGroup?.showMergeSubscriberGroup;

    return <div className="entity-view">
      <Paper componentId="external-url-paper" className={undefined}>
        {!!this.props.shouldDisplayModal &&
          <PromptConfirmation
            title={translation.cancel_changes}
            description={translation.are_you_sure}
            continueText={translation.undo_all}
            cancelText={translation.go_back}
            onContinue={this.props.continue}
            onCancel={this.props.cancel} />}

        {!!displayMergeSubscriberGroupModal &&
          <MergeSubscriberGroupModal
            mergeSubscriberGroup={{
              ...mergeSubscriberGroup,
              id: this.props.form.subscriberGroupItem.id,
              name: this.props.form.subscriberGroupItem.description
            }}
            options={this.props.form.subscriberGroupOptions}
            translation={translation}
            validation={this.props.form.validation}
            deleteMergeSubscriberGroupItem={this.deleteSubscriberGroupItem}
            onClose={this.mergeSubscriberGroupItem}
            updateMergeSubscriberGroup={this.props.updateMergeSubscriberGroup}
            loadOptions={this.props.loadSubscriberGroupOptions}
            resetValidation={this.resetValidation}
            updateValidation={this.props.updateValidation} />}

        {!form.languageOptions || !form.subscriberGroupItem || form.fetching
          ? <SpinLoader />
          : <SubscriberGroupFormSections
            actionType={SubscriberGroupActionType[actionType]}
            formState={form}
            shouldPrompt={shouldPrompt}
            mainLanguage={mainLanguage}
            saveSubscriberGroup={this.saveSubscriberGroup}
            updateItem={updateItem}
            changeLanguage={changeLanguage}
            copyLanguageToAll={copyLanguageToAll}
            changeLongDescription={changeLongDescription}
            copyLongDescriptionToAll={copyLongDescriptionToAll}
            options={options}
            updateValidation={updateValidation}
            displayError={displayError}
            section={section}
            locale={locale}
            translation={translation}
            showTranslations={this.state.showTranslations}
            switchTranslationsToggle={this.switchTranslationsToggle}
            updateExternalMemberItem={updateExternalMemberItem}
            removeExternalMember={removeExternalMember}
            addExternalMember={addExternalMember}
            mergeSubscriberGroupItem={this.mergeSubscriberGroupItem}
            resetValidation={resetValidation}
            removeAllExternalMembers={removeAllExternalMembers} />}
      </Paper>
    </div>;
  }
}

const container = containerFactory(
  withPrompt(withOptions(SubscriberGroupFormComponent)),
  actionCreators, selectors, subscriberGroupsTranslation
);

export { container as SubscriberGroupForm };
